import { supabase } from '../lib/supabase';

class AdminService {
  /**
   * Add credits to a verified user account
   * @param {string} userId - User ID to add credits to
   * @param {number} credits - Number of credits to add
   * @param {string} reason - Reason for adding credits
   * @returns {Promise<object>} Updated user profile
   */
  async addCreditsToUser(userId, credits, reason = 'Admin credit grant') {
    try {
      // First, verify the user exists and get their current data
      const { data: userProfile, error: fetchError } = await supabase?.from('user_profiles')?.select('id, credits_remaining, verification_status, role, full_name, email')?.eq('id', userId)?.maybeSingle();

      if (fetchError) {
        console.error('Error fetching user profile:', fetchError);
        throw new Error(`Database error: ${fetchError?.message}`);
      }

      if (!userProfile) {
        throw new Error('User not found. Please verify the user ID is correct.');
      }

      // Check if user is verified
      if (userProfile?.verification_status !== 'verified') {
        throw new Error(`Cannot add credits: User account is ${userProfile?.verification_status}. Only verified accounts can receive credits.`);
      }

      // Calculate new credit balance
      const currentCredits = userProfile?.credits_remaining || 0;
      const newCredits = currentCredits + credits;

      // Update user credits
      const { data: updatedProfile, error: updateError } = await supabase?.from('user_profiles')?.update({ 
          credits_remaining: newCredits,
          updated_at: new Date()?.toISOString()
        })?.eq('id', userId)?.select()?.maybeSingle();

      if (updateError) {
        console.error('Error updating credits:', updateError);
        throw new Error(`Failed to update credits: ${updateError?.message}`);
      }

      if (!updatedProfile) {
        throw new Error('Failed to update user profile. Please try again.');
      }

      // Log the credit addition in activity_logs
      const { error: logError } = await supabase?.from('activity_logs')?.insert({
          user_id: userId,
          activity_type: 'credit_added',
          activity_description: `${credits} credits added by admin. Reason: ${reason}`,
          metadata: {
            credits_added: credits,
            previous_balance: currentCredits,
            new_balance: newCredits,
            reason: reason
          }
        });

      if (logError) {
        console.error('Error logging credit addition:', logError);
        // Don't throw here - credits were added successfully
      }

      return {
        success: true,
        user: updatedProfile,
        message: `Successfully added ${credits} credits to ${userProfile?.full_name}. New balance: ${newCredits} credits`
      };
    } catch (error) {
      console.error('Error in addCreditsToUser:', error);
      throw error;
    }
  }

  /**
   * Get all verified users eligible for credit grants (all roles)
   * @returns {Promise<array>} List of verified users with value/label format for dropdowns
   */
  async getVerifiedUsers() {
    try {
      const { data, error } = await supabase?.from('user_profiles')?.select('id, full_name, email, credits_remaining, subscription_tier, verification_status, role')?.eq('verification_status', 'verified')?.order('full_name', { ascending: true });

      if (error) throw error;

      // Transform data into dropdown-friendly format
      return (data || [])?.map(user => ({
        value: user?.id,
        label: `${user?.full_name} (${user?.email}) - ${user?.role === 'admin' ? 'Admin' : `${user?.credits_remaining || 0} credits`}`,
        credits: user?.credits_remaining || 0,
        email: user?.email,
        name: user?.full_name,
        role: user?.role
      }));
    } catch (error) {
      console.error('Error fetching verified users:', error);
      throw error;
    }
  }

  /**
   * Get credit history for a user
   * @param {string} userId - User ID
   * @returns {Promise<array>} Credit transaction history
   */
  async getCreditHistory(userId) {
    try {
      const { data, error } = await supabase?.from('activity_logs')?.select('*')?.eq('user_id', userId)?.in('activity_type', ['credit_added', 'credit_purchase', 'credit_used'])?.order('created_at', { ascending: false })?.limit(50);

      if (error) throw error;

      return data || [];
    } catch (error) {
      console.error('Error fetching credit history:', error);
      throw error;
    }
  }

  /**
   * Get accurate dashboard statistics
   * @returns {Promise<object>} Dashboard statistics
   */
  async getDashboardStats() {
    try {
      const { data, error } = await supabase?.rpc('get_admin_dashboard_stats');

      if (error) throw error;

      return {
        totalUsers: Number(data?.[0]?.total_users || 0),
        pendingApprovals: Number(data?.[0]?.pending_approvals || 0),
        activeToday: Number(data?.[0]?.active_today || 0),
        totalRecommendations: Number(data?.[0]?.total_recommendations || 0),
        verifiedUsers: Number(data?.[0]?.verified_users || 0),
        totalActiveSessions: Number(data?.[0]?.total_active_sessions || 0)
      };
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
      throw error;
    }
  }

  /**
   * Get all active users with comprehensive data
   * @returns {Promise<array>} List of all active users with analytics
   */
  async getAllActiveUsers() {
    try {
      const { data, error } = await supabase?.rpc('get_all_active_users');

      if (error) throw error;

      return data?.map(user => ({
        id: user?.id,
        name: user?.full_name,
        email: user?.email,
        role: user?.role,
        specialty: user?.specialization || 'Not specified',
        institution: user?.institution_name || 'Not specified',
        institutionCountry: user?.institution_country,
        subscriptionTier: this.formatSubscriptionTier(user?.subscription_tier),
        creditBalance: user?.credits_remaining || 0,
        verificationStatus: user?.verification_status,
        status: this.getUserStatus(user),
        lastActive: this.formatLastActive(user?.last_sign_in_at),
        lastActiveRaw: user?.last_sign_in_at,
        totalRecommendations: user?.total_recommendations || 0,
        accountCreatedAt: user?.account_created_at,
        totalSessions: Number(user?.total_sessions || 0),
        totalUsageMinutes: Number(user?.total_usage_minutes || 0),
        lastSessionDate: user?.last_session_date,
        profileImage: user?.avatar_url || '/assets/images/no_image.png',
        profileImageAlt: `${user?.full_name} profile picture`
      })) || [];
    } catch (error) {
      console.error('Error fetching all active users:', error);
      throw error;
    }
  }

  /**
   * Format subscription tier for display
   * @private
   */
  formatSubscriptionTier(tier) {
    if (!tier) return 'Free';
    return tier?.charAt(0)?.toUpperCase() + tier?.slice(1);
  }

  /**
   * Determine user status based on last sign in
   * @private
   */
  getUserStatus(user) {
    if (user?.verification_status !== 'verified') return 'suspended';
    
    const lastSignIn = user?.last_sign_in_at ? new Date(user?.last_sign_in_at) : null;
    if (!lastSignIn) return 'trial';
    
    const daysSinceLastSignIn = (Date.now() - lastSignIn?.getTime()) / (1000 * 60 * 60 * 24);
    
    if (daysSinceLastSignIn <= 7) return 'active';
    return 'trial';
  }

  /**
   * Format last active time for display
   * @private
   */
  formatLastActive(lastSignIn) {
    if (!lastSignIn) return 'Never';
    
    const date = new Date(lastSignIn);
    const now = new Date();
    const diffInMinutes = Math.floor((now - date) / (1000 * 60));
    
    if (diffInMinutes < 60) {
      return `${diffInMinutes} min${diffInMinutes !== 1 ? 's' : ''} ago`;
    }
    
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) {
      return `${diffInHours} hour${diffInHours !== 1 ? 's' : ''} ago`;
    }
    
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 30) {
      return `${diffInDays} day${diffInDays !== 1 ? 's' : ''} ago`;
    }
    
    return date?.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  }
}

export default new AdminService();